import { useState, useEffect } from "react";
import SectionEyebrow from "@components/common/SectionEyebrow";
import ThemedIcon from "@components/common/ThemedIcon";
import useInView from "@hooks/useInView";
import { useCases } from "@data/useCases";

const DURATION = 4500;

export default function UseCasesSection() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [barWidth, setBarWidth] = useState(0);
  const [sectionRef, inView] = useInView({ threshold: 0.1 });
  const active = useCases[activeIdx];

  useEffect(() => {
    if (!autoPlay || !inView) return;

    setBarWidth(0);
    const startBar = setTimeout(() => setBarWidth(100), 40);
    const advance = setTimeout(() => {
      setActiveIdx((prev) => (prev + 1) % useCases.length);
    }, DURATION);

    return () => {
      clearTimeout(startBar);
      clearTimeout(advance);
    };
  }, [activeIdx, autoPlay, inView]);

  const handleSelect = (i) => {
    setAutoPlay(false);
    setBarWidth(0);
    setActiveIdx(i);
  };

  return (
    <section
      ref={sectionRef}
      className="relative bg-konecta-black content-px py-section overflow-hidden border-t border-white/[0.1]"
      id="use-cases"
    >
      {/* Background accent */}
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-konecta-orange/[0.03] blur-[120px] rounded-full pointer-events-none" />

      {/* Header */}
      <div
        className={`relative z-10 mb-16 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      >
        <SectionEyebrow text="Use Cases" />
        <h2 className="section-title mt-4 max-w-3xl">
          Real Challenges.
          <br />
          <span className="text-gradient-orange">Real Outcomes.</span>
        </h2>
        <p className="text-base text-white/80 mt-4 max-w-2xl">
          How Konecta has delivered for enterprise and government clients across
          South Africa.
        </p>
      </div>

      {/* Case selector tabs */}
      <div
        className={`relative z-10 transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
      >
        <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
          {useCases.map((uc, i) => (
            <button
              key={uc.id}
              onClick={() => handleSelect(i)}
              className={`relative flex-1 min-w-[140px] px-6 lg:px-10 py-5 rounded-xl text-left transition-all duration-300 cursor-pointer border overflow-hidden ${
                i === activeIdx
                  ? "glass-card-orange text-konecta-white"
                  : "bg-transparent border-white/[0.06] text-konecta-muted hover:border-white/[0.15] hover:text-konecta-white"
              }`}
            >
              <ThemedIcon name={uc.icon} size={28} className="mb-2" />
              <div className="text-[0.7rem] uppercase tracking-wider mt-1 whitespace-nowrap">
                {uc.category}
              </div>

              {/* Auto-play progress bar */}
              {i === activeIdx && autoPlay && (
                <div
                  className="absolute bottom-0 left-0 h-[3px] bg-konecta-orange rounded-full"
                  style={{
                    width: `${barWidth}%`,
                    transition:
                      barWidth === 100
                        ? `width ${DURATION - 40}ms linear`
                        : "none",
                  }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Hint + dots row */}
        <div className="flex items-center justify-between mb-10 px-0.5">
          <p className="text-[0.7rem] text-white/35 uppercase tracking-wider">
            {autoPlay ? "Auto-advancing — select a topic to explore" : "Select a topic to explore"}
          </p>
          <div className="flex items-center gap-2">
            {useCases.map((_, i) => (
              <button
                key={i}
                onClick={() => handleSelect(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === activeIdx
                    ? "w-5 h-1.5 bg-konecta-orange"
                    : "w-1.5 h-1.5 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Active use case */}
      <div
        key={active.id}
        className="relative z-10 glass-card p-8 lg:p-10 animate-fade-in"
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="px-3 py-1 rounded-full text-[0.65rem] font-bold uppercase tracking-widest text-konecta-orange border border-konecta-orange/20 bg-konecta-orange/[0.05]">
            {active.service}
          </span>
        </div>

        <h3
          className="font-heading font-extrabold text-konecta-white leading-tight mb-6"
          style={{ fontSize: "clamp(1.25rem, 2vw, 1.75rem)" }}
        >
          {active.title}
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Challenge */}
          <div>
            <h4 className="font-heading text-xs font-bold uppercase tracking-widest text-konecta-white mb-3">
              The Challenge
            </h4>
            <p className="text-base text-white/85 leading-relaxed">
              {active.challenge}
            </p>
          </div>

          {/* Outcomes */}
          <div>
            <h4 className="font-heading text-xs font-bold uppercase tracking-widest text-konecta-white mb-3">
              Outcomes
            </h4>
            <ul className="space-y-2">
              {active.outcomes.map((outcome) => (
                <li
                  key={outcome}
                  className="flex items-start gap-2 text-base text-white/85"
                >
                  <span className="text-konecta-orange mt-0.5">&#x2713;</span>
                  {outcome}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
